import { useState } from "react";
import { jobsApi, staffDocsApi } from "@/api";
import { Button, SelectField, TextAreaField, TextField } from "@/components/ui";
import { isContainerSet } from "@/lib/jobs";
import { FormDrawer } from "@/staff/jobs/FormDrawer";
import type { DocumentShare } from "@/types";

/** Upload & share flow (§4.4): upload the file (multipart, 292) → create the
 *  share (S1, with optional message + due date) → optionally Send (S2).
 *  Sharing never creates a production drawing — the file is stored in the job's
 *  "Client documents" container set (resolved lazily at submit; a plain
 *  non-current set the recompute and the Drawing sets tab both ignore).
 *  Versioning lives at the share level: a revision is uploaded as a NEW file
 *  and S1 carries supersedes_share_id — S2 then flips the predecessor
 *  automatically (as-built §4.2). A for-information share (requires_approval
 *  false, as-built §9.13) never asks the client to decide, so the due-date
 *  field disappears with it — S1 would zero it anyway. Reuses the job-drawer
 *  shell + styles (always in the bundle via the jobs pages). */
export function ShareDrawer({
  jobId,
  supersedes,
  onClose,
  onDone,
}: {
  jobId: number;
  /** When set, this upload supersedes the given share. */
  supersedes: DocumentShare | null;
  onClose: () => void;
  onDone: (result: "draft" | "sent" | "send_failed") => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  // A revision keeps its predecessor's type unless the staff user changes it.
  const [requiresApproval, setRequiresApproval] = useState(supersedes?.requires_approval ?? true);
  const [dueBy, setDueBy] = useState(""); // yyyy-mm-dd
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onPickFile(f: File | null) {
    setFile(f);
    if (f && !title.trim()) setTitle(f.name);
  }

  /** The job's container set for shared documents, created on first share. */
  async function resolveContainerSetId(): Promise<number> {
    const detail = await jobsApi.getJobDetail(jobId);
    const existing = detail.drawing_sets.find(isContainerSet);
    if (existing) return existing.id;
    const created = await staffDocsApi.createContainerSet(jobId, "Client documents");
    return created.id;
  }

  async function submit(sendNow: boolean) {
    if (!file) {
      setError("Choose a PDF to upload.");
      return;
    }
    if (file.type && file.type !== "application/pdf") {
      setError("The client app renders PDFs — please upload a PDF.");
      return;
    }
    if (sendNow && !window.confirm("Send this document to the client now?")) return;

    setBusy(true);
    setError(null);
    const shareTitle = title.trim() || file.name;
    let uploadedId: number | null = null;
    try {
      const setId = await resolveContainerSetId();
      const up = await staffDocsApi.uploadDrawing({
        file,
        jobId,
        drawingSetId: setId,
        title: shareTitle,
      });
      uploadedId = up.id;
      const share = await staffDocsApi.createShare({
        job_id: jobId,
        drawing_id: up.id,
        title: shareTitle,
        message: message.trim() || undefined,
        requires_approval: requiresApproval,
        due_by:
          requiresApproval && dueBy ? Date.parse(`${dueBy}T12:00:00`) : undefined,
        supersedes_share_id: supersedes?.share_id,
      });
      if (!sendNow) {
        onDone("draft");
        return;
      }
      try {
        await staffDocsApi.sendShare(share.id);
        onDone("sent");
      } catch {
        // The draft exists and is valid — surface it rather than unwinding.
        onDone("send_failed");
      }
    } catch (err) {
      // If the share never got created, tidy the orphan drawing (best-effort;
      // the nightly reconciliation would catch it anyway).
      if (uploadedId !== null) {
        void staffDocsApi.deleteDrawing(uploadedId).catch(() => undefined);
      }
      setError(err instanceof Error ? err.message : "The upload failed.");
      setBusy(false);
    }
  }

  return (
    <FormDrawer
      title={supersedes ? "Upload revision" : "Upload & share"}
      onClose={onClose}
      busy={busy}
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => void submit(false)} disabled={busy || !file}>
            Save as draft
          </Button>
          <Button onClick={() => void submit(true)} loading={busy} disabled={!file}>
            Upload &amp; send
          </Button>
        </>
      }
    >
      {supersedes ? (
        <p className="share-drawer__supersedes">
          Supersedes <strong>{supersedes.title}</strong> — sending flips the old share
          automatically.
        </p>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="share-file">
          File (PDF)
        </label>
        <input
          id="share-file"
          type="file"
          accept="application/pdf"
          className="share-drawer__file"
          onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
          disabled={busy}
        />
      </div>

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Defaults to the file name"
        disabled={busy}
      />

      <TextAreaField
        label="Message to the client"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        disabled={busy}
      />

      <SelectField
        label="Share type"
        value={requiresApproval ? "approval" : "info"}
        onChange={(e) => setRequiresApproval(e.target.value === "approval")}
        hint={
          requiresApproval
            ? "The client is asked to approve or request changes."
            : "For information only — the client can view it but is never asked to decide."
        }
        disabled={busy}
      >
        <option value="approval">Needs client approval</option>
        <option value="info">For information only</option>
      </SelectField>

      {requiresApproval ? (
        <TextField
          label="Response due by"
          type="date"
          value={dueBy}
          onChange={(e) => setDueBy(e.target.value)}
          disabled={busy}
        />
      ) : null}

      {error ? (
        <p className="share-drawer__error" role="alert">
          {error}
        </p>
      ) : null}
    </FormDrawer>
  );
}
